import { client } from '../client';
import { getApiConfig } from '../config/api';

// Chat Backend API Configuration
const config = getApiConfig();
const CHAT_API_BASE_URL = config.CHAT_API_BASE_URL;

// Chat API functions
export const chatAPI = {
  // Initialize a new chat session
  initSession: async (userToken = null) => {
    try {
      const response = await fetch(`${CHAT_API_BASE_URL}/api/chat/init`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token: userToken }),
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        data: {
          sessionId: data.sessionId,
          userToken: data.userToken,
          userInfo: data.userInfo || null, // Handle user info from init response
          rateLimits: data.rateLimits || {},
          timestamp: data.timestamp
        },
        headers: {
          // Only daily rate limit headers now
          dailyLimit: response.headers.get('X-RateLimit-Daily-Limit'),
          dailyRemaining: response.headers.get('X-RateLimit-Daily-Remaining'),
          dailyUsed: response.headers.get('X-RateLimit-Daily-Used'),
          resetTime: response.headers.get('X-RateLimit-Reset'),
        }
      };
    } catch (error) {
      console.error('Error initializing chat session:', error);
      throw error;
    }
  },

  // Send a message to the AI assistant
  sendMessage: async (message, sessionId, userToken) => {
    try {
      const response = await fetch(`${CHAT_API_BASE_URL}/api/chat`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          message,
          sessionId,
          userToken,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return {
        data: {
          message: data.message,
          sessionId: data.sessionId,
          userInfo: data.userInfo || null, // Handle user info from chat response
          rateLimits: data.rateLimits || {}
        },
        headers: {
          // Only daily rate limit headers now
          dailyLimit: response.headers.get('X-RateLimit-Daily-Limit'),
          dailyRemaining: response.headers.get('X-RateLimit-Daily-Remaining'),
          dailyUsed: response.headers.get('X-RateLimit-Daily-Used'),
          resetTime: response.headers.get('X-RateLimit-Reset'),
        }
      };
    } catch (error) {
      console.error('Error sending message:', error);
      throw error;
    }
  },

  // Send a message using SSE streaming
  sendMessageStream: async (message, sessionId, userToken, onChunk) => {
    const response = await fetch(`${CHAT_API_BASE_URL}/api/chat/stream`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ message, sessionId, userToken }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;

      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop();

      for (const line of lines) {
        if (line.startsWith('data: ')) {
          try {
            onChunk(JSON.parse(line.slice(6)));
          } catch (_) {}
        }
      }
    }
  },

  // Transcribe recorded audio via the backend (Groq Whisper)
  transcribeAudio: async (audioBlob) => {
    const response = await fetch(`${CHAT_API_BASE_URL}/api/transcribe`, {
      method: 'POST',
      headers: { 'Content-Type': audioBlob.type || 'audio/webm' },
      body: audioBlob,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data.text || '';
  },

  // Check rate limit status
  getStatus: async (sessionId, userToken) => {
    try {
      const response = await fetch(`${CHAT_API_BASE_URL}/api/chat/status?sessionId=${sessionId}&userToken=${userToken}`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error getting chat status:', error);
      throw error;
    }
  },

  // Health check
  healthCheck: async () => {
    try {
      const response = await fetch(`${CHAT_API_BASE_URL}/health`);
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error checking health:', error);
      throw error;
    }
  },
};

// Consolidated query to fetch all data in one request
export const fetchAllData = async () => {
  const query = `{
    "abouts": *[_type == "abouts"],
    "skills": *[_type == "skills"],
    "experiences": *[_type == "experiences"],
    "works": *[_type == "works"],
    "achievements": *[_type == "achievements"]
  }`;

  try {
    const data = await client.fetch(query);
    return data;
  } catch (error) {
    console.error('Error fetching data:', error);
    throw error;
  }
};

// Individual queries for backward compatibility (if needed)
export const fetchAbouts = async () => {
  const query = '*[_type == "abouts"]';
  return await client.fetch(query);
};

export const fetchSkills = async () => {
  const query = '*[_type == "skills"]';
  return await client.fetch(query);
};

export const fetchExperiences = async () => {
  const query = '*[_type == "experiences"]';
  return await client.fetch(query);
};

export const fetchWorks = async () => {
  const query = '*[_type == "works"]';
  return await client.fetch(query);
};

export const fetchAchievements = async () => {
  const query = '*[_type == "achievements"]';
  return await client.fetch(query);
}; 